/**
 * ChatQuickActions Component
 *
 * Displays quick action buttons for common workflow operations.
 * Helps users get started quickly with pre-defined workflow patterns.
 */

import React from 'react';
import {
  Zap,
  Mail,
  Clock,
  Database,
  Webhook,
  Bell,
  GitBranch,
  MessageSquare,
  HelpCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Quick action definition
 */
export interface QuickAction {
  /** Unique identifier */
  id: string;
  /** Display label */
  label: string;
  /** Message to send when clicked */
  message: string;
  /** Icon component */
  icon: React.ReactNode;
  /** Optional color class */
  colorClass?: string;
}

/**
 * Default quick actions
 */
const DEFAULT_QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'data-triggered',
    label: 'Data Triggered',
    message: 'Create a workflow that triggers when data is received from a device',
    icon: <Zap className="w-4 h-4" />,
    colorClass: 'text-yellow-600 dark:text-yellow-400',
  },
  {
    id: 'scheduled',
    label: 'Scheduled',
    message: 'Create a scheduled workflow that runs on a timer',
    icon: <Clock className="w-4 h-4" />,
    colorClass: 'text-blue-600 dark:text-blue-400',
  },
  {
    id: 'webhook',
    label: 'Webhook',
    message: 'Create a workflow that triggers from an HTTP webhook',
    icon: <Webhook className="w-4 h-4" />,
    colorClass: 'text-purple-600 dark:text-purple-400',
  },
  {
    id: 'email-notification',
    label: 'Send Email',
    message: 'Add an email notification to my workflow',
    icon: <Mail className="w-4 h-4" />,
    colorClass: 'text-green-600 dark:text-green-400',
  },
  {
    id: 'store-data',
    label: 'Store Data',
    message: 'Add a node to store data in the database',
    icon: <Database className="w-4 h-4" />,
    colorClass: 'text-red-600 dark:text-red-400',
  },
  {
    id: 'condition',
    label: 'Add Condition',
    message: 'Add a conditional branch to check values',
    icon: <GitBranch className="w-4 h-4" />,
    colorClass: 'text-orange-600 dark:text-orange-400',
  },
  {
    id: 'mqtt',
    label: 'MQTT Message',
    message: 'Create a workflow triggered by MQTT messages',
    icon: <MessageSquare className="w-4 h-4" />,
    colorClass: 'text-cyan-600 dark:text-cyan-400',
  },
  {
    id: 'help',
    label: 'Help',
    message: 'help',
    icon: <HelpCircle className="w-4 h-4" />,
    colorClass: 'text-gray-600 dark:text-gray-400',
  },
];

interface ChatQuickActionsProps {
  /** Callback when an action is clicked */
  onActionClick: (message: string) => void;
  /** Custom actions to display (overrides defaults if provided) */
  actions?: QuickAction[];
  /** Whether to show compact (smaller) buttons */
  compact?: boolean;
  /** Additional class names */
  className?: string;
  /** Whether the actions are disabled */
  disabled?: boolean;
}

/**
 * ChatQuickActions component
 */
export const ChatQuickActions: React.FC<ChatQuickActionsProps> = ({
  onActionClick,
  actions = DEFAULT_QUICK_ACTIONS,
  compact = false,
  className,
  disabled = false,
}) => {
  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-xs text-muted-foreground font-medium">
        Quick actions:
      </p>
      <div className="flex flex-wrap gap-2">
        {actions.map((action) => (
          <Button
            key={action.id}
            variant="outline"
            size={compact ? 'sm' : 'default'}
            className={cn(
              'text-xs',
              compact ? 'h-7 px-2' : 'h-8 px-3',
              action.colorClass
            )}
            onClick={() => onActionClick(action.message)}
            disabled={disabled}
          >
            <span className="mr-1.5">{action.icon}</span>
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

/**
 * Contextual quick actions based on workflow state
 */
export const getContextualActions = (options: {
  hasNodes: boolean;
  hasTrigger: boolean;
  selectedNodeType?: string;
}): QuickAction[] => {
  const { hasNodes, hasTrigger, selectedNodeType } = options;

  // If no nodes, suggest starting actions
  if (!hasNodes) {
    return [
      {
        id: 'data-triggered',
        label: 'Start with Data Trigger',
        message: 'Create a workflow that triggers when data is received',
        icon: <Zap className="w-4 h-4" />,
        colorClass: 'text-yellow-600',
      },
      {
        id: 'scheduled',
        label: 'Start with Schedule',
        message: 'Create a scheduled workflow',
        icon: <Clock className="w-4 h-4" />,
        colorClass: 'text-blue-600',
      },
      {
        id: 'webhook',
        label: 'Start with Webhook',
        message: 'Create a webhook-triggered workflow',
        icon: <Webhook className="w-4 h-4" />,
        colorClass: 'text-purple-600',
      },
    ];
  }

  // If has nodes but no trigger, suggest adding trigger
  if (!hasTrigger) {
    return [
      {
        id: 'add-trigger',
        label: 'Add Trigger',
        message: 'Add a trigger to start this workflow',
        icon: <Zap className="w-4 h-4" />,
        colorClass: 'text-yellow-600',
      },
    ];
  }

  // Context-specific actions based on selected node
  if (selectedNodeType) {
    const contextActions: QuickAction[] = [];

    // After a trigger, suggest actions
    if (selectedNodeType.includes('trigger') || selectedNodeType === 'device_data_received') {
      contextActions.push(
        {
          id: 'add-condition',
          label: 'Add Condition',
          message: 'Add a condition after the trigger',
          icon: <GitBranch className="w-4 h-4" />,
          colorClass: 'text-orange-600',
        },
        {
          id: 'add-store',
          label: 'Store Data',
          message: 'Store the received data',
          icon: <Database className="w-4 h-4" />,
          colorClass: 'text-red-600',
        }
      );
    }

    // After a condition, suggest branches
    if (selectedNodeType === 'condition') {
      contextActions.push(
        {
          id: 'add-notification',
          label: 'Add Notification',
          message: 'Send a notification when condition is true',
          icon: <Bell className="w-4 h-4" />,
          colorClass: 'text-green-600',
        }
      );
    }

    if (contextActions.length > 0) {
      return contextActions;
    }
  }

  // Default actions for existing workflows
  return [
    {
      id: 'add-notification',
      label: 'Add Notification',
      message: 'Add an email or SMS notification',
      icon: <Mail className="w-4 h-4" />,
      colorClass: 'text-green-600',
    },
    {
      id: 'add-condition',
      label: 'Add Condition',
      message: 'Add a conditional branch',
      icon: <GitBranch className="w-4 h-4" />,
      colorClass: 'text-orange-600',
    },
    {
      id: 'add-delay',
      label: 'Add Delay',
      message: 'Add a delay before the next action',
      icon: <Clock className="w-4 h-4" />,
      colorClass: 'text-blue-600',
    },
  ];
};

export default ChatQuickActions;
